/**
 * localStorage helpers for the in-app feedback prompt.
 *
 * Keys:
 *  - feedback:submitted     — '1' once the user has submitted feedback (stop auto-prompting)
 *  - feedback:snoozedUntil  — epoch ms until which the proactive modal stays hidden
 *  - feedback:engagement    — counter of app sessions (a proxy for "came back a few times")
 *  - feedback:hasGenerated  — '1' once the user has run at least one AI generation
 *
 * sessionStorage keys:
 *  - feedback:sessionCounted — '1' if this browser session already bumped the counter
 *
 * All access is wrapped in try/catch so private mode / disabled storage never throws.
 */

const SUBMITTED_KEY = 'feedback:submitted';
const SNOOZED_UNTIL_KEY = 'feedback:snoozedUntil';
const ENGAGEMENT_KEY = 'feedback:engagement';
const HAS_GENERATED_KEY = 'feedback:hasGenerated';
const SESSION_COUNTED_KEY = 'feedback:sessionCounted';

/** Number of sessions before the proactive modal becomes eligible. */
export const ENGAGEMENT_THRESHOLD = 3;

/** Days the modal stays hidden after the user dismisses it. */
export const SNOOZE_DAYS = 30;

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

export function isSubmitted(): boolean {
  return safeGet(SUBMITTED_KEY) === '1';
}

export function markSubmitted(): void {
  safeSet(SUBMITTED_KEY, '1');
}

export function isSnoozed(): boolean {
  const raw = safeGet(SNOOZED_UNTIL_KEY);
  if (!raw) return false;
  const until = Number(raw);
  return Number.isFinite(until) && until > Date.now();
}

export function snooze(days = SNOOZE_DAYS): void {
  safeSet(SNOOZED_UNTIL_KEY, String(Date.now() + days * 24 * 60 * 60 * 1000));
}

/** True once the user has started at least one AI generation in this browser. */
export function hasGenerated(): boolean {
  return safeGet(HAS_GENERATED_KEY) === '1';
}

/** Called from the API layer when a generation request succeeds. */
export function markGenerated(): void {
  safeSet(HAS_GENERATED_KEY, '1');
}

/**
 * Count the current browser session towards engagement — at most once per tab,
 * however many times the React tree mounts — and return the resulting count.
 *
 * The sessionStorage guard is the load-bearing part: the counter must mean
 * "came back N times", and a component remount is not a return visit.
 */
export function registerSession(): number {
  const current = Number(safeGet(ENGAGEMENT_KEY)) || 0;

  if (safeSessionGet(SESSION_COUNTED_KEY) === '1') {
    return current;
  }

  const next = current + 1;
  safeSet(ENGAGEMENT_KEY, String(next));
  safeSessionSet(SESSION_COUNTED_KEY, '1');
  return next;
}
